import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { DividerComponent } from '@shared/components/divider.component';
import { TitleContainerComponent } from '@shared/components/title-container.component';
import { TreatyTableComponent } from '../treaty-table/treaty-table.component';
import { TreatyStatsDetailComponent } from '../treaty-stats-detail/treaty-stats-detail.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TreatyApiService } from '../services/treaty-api.service';
import { propertyColDefs } from '../treaty-table.config';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TreatyWithStats } from '../models/treaty-with-stats.model';
import { TreatyProperty } from '../models/treaty-property.model';
import { distinctUntilChanged, EMPTY, finalize, interval, startWith, switchMap, takeWhile, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getFilenameFromContentDisposition, saveFile } from '@shared/utils/file.utils';
import { AsyncState, AsyncStateEnum } from '@shared/models/async-state.enum';

@Component({
  selector: 'app-treaty-property-detail',
  imports: [
    TitleContainerComponent,
    MatButtonModule,
    MatIconModule,
    DividerComponent,
    MatTabsModule,
    MatProgressBarModule,
    TreatyTableComponent,
    TreatyStatsDetailComponent,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './treaty-property-detail.component.html',
  styleUrl: './treaty-property-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreatyPropertyDetailComponent {
  treatyApiService = inject(TreatyApiService);

  treatyStateEnum = AsyncStateEnum;

  isLoading = signal(true);
  isLoadingExport = signal(false);

  treatyState = signal<AsyncState>(this.treatyStateEnum.FINISHED);
  readonly treatyId = input.required<number>();

  propertyColDefs = propertyColDefs;
  propertyDefaultColDef = {};

  private isPoolingActive = true;

  propertyWithStats = toSignal<TreatyWithStats<TreatyProperty>>(
    toObservable(this.treatyId).pipe(
      tap(() => this.isLoading.set(true)),
      switchMap(id =>
        interval(5000).pipe(
          startWith(0),
          takeWhile(() => this.isPoolingActive),
          switchMap(() => this.treatyApiService.getTreatyById$(id)),
          distinctUntilChanged((prev, curr) => prev.treatyState === curr.treatyState)
        )
      ),
      switchMap(treaty => {
        if (treaty.treatyState === this.treatyStateEnum.IN_PROGRESS) {
          this.treatyState.set(treaty.treatyState);
        } else if (treaty.treatyState === this.treatyStateEnum.FINISHED) {
          this.isPoolingActive = false;
          this.treatyState.set(treaty.treatyState);
          return this.treatyApiService.getPropertyWithStatsById$(treaty.treatyId).pipe(finalize(() => this.isLoading.set(false)));
        } else {
          this.isPoolingActive = false;
          this.treatyState.set(this.treatyStateEnum.ERROR);
          this.isLoading.set(false);
        }
        return EMPTY;
      })
    )
  );

  createReferral() {
    window.open(`${environment.esgReferralLink}/create`, '_blank');
  }

  exportExcel() {
    this.isLoadingExport.set(true);
    this.treatyApiService
      .exportPropertyWithStatsById$(this.treatyId())
      .pipe(
        tap(response => {
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = getFilenameFromContentDisposition(contentDisposition) ?? 'downloaded-file';
          if (response.body) {
            saveFile(response.body, filename);
          }
        }),
        finalize(() => {
          this.isLoadingExport.set(false);
        })
      )
      .subscribe();
  }
}
